import {
  ExtraItemCategory,
  InsuranceName,
  Nullable,
  PreReservationWithPaymentDto,
  ReservationInsuranceInfoWithPreReservationDto,
  ReservationState,
  translateVehicleModelType,
} from '@orcar/common';
import {
  Button,
  CancelInfo,
  CancelModal,
  CarOption,
  CommonModal,
  ConfiguredInsuranceInfo,
  DriverPaymentInfo,
  getAvailableVehicleOptionsArray,
  getModelFuel,
  getReservationInsuranceLabel,
  getTransmissionText,
  isMobileAtom,
  MUTATION_KEY,
  OptionalService,
  QUERY_KEY,
  RefundInfo,
  RentalCondition,
  RentInfo,
  Styles,
  TOAST_MESSAGE_TYPE,
  useCancelReservation,
  useGetCompanyInfo,
  useGetRefundPolicyForPreReservation,
  useRecoil,
  useToast,
} from '@orcar/common-d2c';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import CancellationFee from '@/components/reservationDetails/CancellationFee';
import { CompanyInfo } from '@/components/reservationDetails/CompanyInfo';
import { PetReservationInfo } from '@/components/reservationDetails/PetReservationInfo';
import RentPolicy from '@/components/reservationDetails/RentPolicy';
import { CAR_SHEET_COMPANY } from '@/constants/common';
import { trackPetReservationViewed } from '@/utils/analytics';
import {
  getInsuranceName,
  getPurchasedInsuranceDisplayType,
} from '@/utils/insurance';
import InsuranceInfo from './InsuranceInfo';
import * as S from '../../styles';

type D2CReservationDetailProp = {
  reservationData: ReservationInsuranceInfoWithPreReservationDto;
  preReservation?: Nullable<PreReservationWithPaymentDto>;
};

const D2CReservationDetail = ({
  reservationData,
  preReservation,
}: D2CReservationDetailProp) => {
  const queryClient = useQueryClient();
  const { recoilValue: isMobile } = useRecoil(isMobileAtom);

  const [cancelReason, setCancelReason] = useState('차량 이용 날짜/시간 변경');
  const [etcText, setEtcText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [cancelInfoOpen, setCancelInfoOpen] = useState(false);
  const [cancelInfoText, setCancelInfoText] = useState('');

  const vehicleModelData = reservationData.vehicleModel;
  const insuranceName =
    preReservation?.insuranceName ||
    reservationData.insuranceInfo?.name ||
    InsuranceName.None;
  const isFullCoverage =
    preReservation?.isFullCoverage ??
    (insuranceName === InsuranceName.Perfect &&
      reservationData.extraItems.some(
        (item) =>
          item.extraItem.category === ExtraItemCategory.SPECIAL_CONTRACT,
      ));
  const insuranceDisplayType = getPurchasedInsuranceDisplayType(
    insuranceName,
    isFullCoverage,
  );
  const { data: companyData } = useGetCompanyInfo();
  const { data: refundPolicyData, error: refundPolicyError } =
    useGetRefundPolicyForPreReservation(preReservation?.id || '');
  const { mutate: cancelReservation } = useCancelReservation();

  const isWithPet = reservationData.isWithPet;
  // 펫과 함께 예약이면 회사 대여규정(DB 텍스트)의 "애완동물 동반 불가" 부분만 제거한다.
  // (낚시용품 동반 불가 안내는 유지)
  const rentalRulesText = isWithPet
    ? (companyData?.rentalRules || '').replace(
        '애완동물 및 낚시용품은',
        '낚시용품은',
      )
    : companyData?.rentalRules || '';

  // 펫 예약 상세 조회 트래킹 (펫 예약일 때 1회)
  useEffect(() => {
    if (isWithPet) {
      trackPetReservationViewed({
        is_with_pet: true,
        pet_type: reservationData.petType,
        pet_count: reservationData.petCount,
        pet_total_weight_kg: reservationData.petTotalWeightKg,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWithPet]);
  const isMutatingCancelReservation =
    queryClient.isMutating({
      mutationKey: [MUTATION_KEY.CANCEL_RESERVATION],
    }) > 0;

  const isCanceled = reservationData.state === ReservationState.CANCELED;
  const isBeforePickUp = dayjs().isBefore(dayjs(reservationData.pickUpAt));
  const isBeforeDropOff = dayjs().isBefore(dayjs(reservationData.dropOffAt));
  const hasPreReservation = !!preReservation;

  const { addToast } = useToast();
  const options = vehicleModelData
    ? [
        translateVehicleModelType(vehicleModelData.type),
        `${vehicleModelData.seat}인승`,
        getTransmissionText(vehicleModelData.transmission),
        getModelFuel(vehicleModelData.fuel),
      ]
    : [];

  const handleClickCancelButton = () => {
    // if (isPickUpToday) {
    //   setCancelInfoText(
    //     `대여 당일 취소는 불가능합니다. 예약실로 연락 부탁드립니다. (${companyData?.phoneNumber})`,
    //   );
    //   setCancelInfoOpen(true);
    //   return;
    // }

    if (
      hasPreReservation &&
      preReservation.d2cPayment &&
      refundPolicyData?.refundAmount &&
      refundPolicyData?.refundPolicy &&
      !refundPolicyError
    ) {
      setModalOpen(true);
      return;
    } else {
      setCancelInfoText(
        `고객센터로 전화주시면 예약 취소를 도와드리겠습니다. (${companyData?.phoneNumber})`,
      );
      setCancelInfoOpen(true);
    }
  };

  const handleCancel = () => {
    if (!reservationData.id || !preReservation) return;

    const reason = !!etcText.length ? etcText : cancelReason;

    cancelReservation(
      {
        input: {
          cancelReason: reason,
          customerName: reservationData.customerName1 || '',
          customerPhoneNumber: reservationData.customerPhoneNumber1 || '',
        },
        id: preReservation.id,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEY.GET_RESERVATION],
          });
          setModalOpen(false);
          addToast({ content: TOAST_MESSAGE_TYPE.CONFIRM_RESERVATION_CANCEL });
        },
        onError: () => {
          setModalOpen(false);
          setCancelInfoText(
            `예약 취소를 실패했습니다. 고객센터로 전화주시면 예약 취소를 도와드리겠습니다. (${companyData?.phoneNumber})`,
          );
          setCancelInfoOpen(true);
        },
      },
    );
  };

  return (
    <ContentWrapper isMobile={isMobile}>
      <RentInfo
        isMobile={isMobile}
        reservationState={reservationData.state}
        reservationId={reservationData.id || ''}
        pickUpDate={dayjs(reservationData.pickUpAt).format()}
        dropOffDate={dayjs(reservationData.dropOffAt).format()}
        options={options}
        vehicleModelImgUrl={vehicleModelData?.images?.[0]?.url || ''}
        vehicleModelName={vehicleModelData?.name || ''}
        companyName={companyData?.name || ''}
        companyPhoneNumber={companyData?.phoneNumber || ''}
      />

      {!isCanceled && (
        <>
          {isBeforeDropOff && (
            <>
              <S.TopLine isMobile={isMobile} />
              <OptionalService
                isMobile={isMobile}
                title='유모차/카시트 안내'
                titleFontSize='20px'
                titleMargin='0 0 30px 0'
                carSheetCompanyName={CAR_SHEET_COMPANY.name}
                carSheetCompanyURL={CAR_SHEET_COMPANY.url}
                carSheetCompanyTelNumber={CAR_SHEET_COMPANY.tel}
              />
            </>
          )}
          <S.ContentWrapper>
            <S.TopLine isMobile={isMobile} />
            <RentalCondition
              isMobile={isMobile}
              isWithPet={isWithPet}
              company={companyData}
              numberOfSeat={vehicleModelData?.seat}
              driverAge={reservationData.insuranceInfo?.minDriverAge}
              drivingExperience={
                reservationData.insuranceInfo?.minDriverExperience
              }
              unavailableItemTextColor={Styles.COLOR.DANGER}
              unavailableItemIconColor={Styles.SVG_FILTER.DANGER}
            />
          </S.ContentWrapper>

          {isWithPet && (
            <>
              <S.TopLine isMobile={isMobile} />
              <PetReservationInfo
                petCount={reservationData.petCount}
                petTotalWeightKg={reservationData.petTotalWeightKg}
                petBreed={reservationData.petBreed}
                isMobile={isMobile}
              />
            </>
          )}

          <S.TopLine isMobile={isMobile} />
          <CarOption
            isMobile={isMobile}
            options={getAvailableVehicleOptionsArray(vehicleModelData)}
          />
          <S.TopLine isMobile={isMobile} />
          {reservationData.preReservation?.pricingVersion === 1 ? (
            <ConfiguredInsuranceInfo info={reservationData.insuranceInfo} />
          ) : (
            <InsuranceInfo
              isFullCoverage={isFullCoverage}
              insurance={insuranceName}
              companyName={companyData?.name || ''}
              coverageLimit={reservationData.insuranceInfo?.limit || ''}
              deductible={reservationData.insuranceInfo?.indemnification || ''}
              lossOfUseFee={reservationData.insuranceInfo?.compensation || ''}
            />
          )}
        </>
      )}
      {hasPreReservation && (
        <>
          <S.TopLine isMobile={isMobile} />
          <DriverPaymentInfo
            listPrice={preReservation.vehiclePrice}
            insurancePrice={preReservation.insurancePrice}
            discountPrice={preReservation.discountPrice}
            totalPrice={preReservation.price}
            paymentMethod={preReservation.d2cPayment?.method || ''}
            easyPayProvider={preReservation.d2cPayment?.easyPayProvider || ''}
            isMobile={isMobile}
          />
        </>
      )}
      {!isCanceled && (
        <>
          <S.TopLine isMobile={isMobile} />
          <CompanyInfo company={companyData} isMobile={isMobile} />
          <S.TopLine isMobile={isMobile} />
          <RentPolicy
            name={companyData?.name || ''}
            rentPolicies={rentalRulesText}
            isMobile={isMobile}
          />

          {isBeforePickUp && (
            <>
              <S.TopLine isMobile={isMobile} />
              <CancellationFee />
              <S.ButtonWrapper>
                <Button
                  width='300px'
                  height='44px'
                  color={Styles.COLOR.WHITE}
                  bgColor={Styles.COLOR.DANGER}
                  borderRadius='5px'
                  font={Styles.NEW_FONT.H5}
                  onClick={handleClickCancelButton}
                >
                  예약 취소하기
                </Button>
              </S.ButtonWrapper>
            </>
          )}
        </>
      )}

      {isCanceled &&
        hasPreReservation &&
        preReservation.d2cPayment &&
        reservationData.finalPrice && (
          <>
            <S.TopLine isMobile={isMobile} />
            <RefundInfo
              price={preReservation.price}
              refundFee={
                preReservation.price - preReservation.d2cPayment.cancelAmount
              }
              isMobile={isMobile}
            />
            <S.TopLine isMobile={isMobile} />
            <CancelInfo />
          </>
        )}

      {modalOpen &&
        reservationData &&
        hasPreReservation &&
        refundPolicyData?.refundPolicy &&
        refundPolicyData?.refundAmount && (
          <CancelModal
            cancelReason={cancelReason}
            etcText={etcText}
            platform='jejuro'
            reservationData={reservationData}
            vehicleModelName={vehicleModelData?.name || ''}
            insuranceName={getReservationInsuranceLabel(
              reservationData,
              getInsuranceName(insuranceDisplayType),
            )}
            refundPolicy={refundPolicyData?.refundPolicy}
            refundAmount={refundPolicyData?.refundAmount}
            setCancelReason={setCancelReason}
            setEtcText={setEtcText}
            onClose={() => setModalOpen(false)}
            onClick={handleCancel}
            isMutatingCancelReservation={isMutatingCancelReservation}
          />
        )}

      {cancelInfoOpen && (
        <CommonModal
          onClick={() => setCancelInfoOpen(false)}
          onClose={() => setCancelInfoOpen(false)}
          hasCancelButton={false}
          mainText={`예약 취소 안내`}
          subText={cancelInfoText}
          buttonText='확인'
        />
      )}
    </ContentWrapper>
  );
};

export default D2CReservationDetail;

const ContentWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${({ isMobile }) => (isMobile ? '100%' : '792px')};
  padding: ${({ isMobile }) =>
    isMobile ? '30px 20px 40px 20px' : '60px 55px'};
  background-color: ${({ theme }) => theme.COLOR.WHITE};
  border: 1px solid ${({ theme }) => theme.COLOR.GREY_05};
  border-radius: 10px;
  box-shadow: ${({ isMobile }) =>
    isMobile && '0px 3px 10px rgba(0, 0, 0, 0.13)'};
`;
